/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';

import {
  Col,
  Form,
  Input,
  InputNumber,
  Layout,
  Radio,
  Row,
  Select,
  Typography,
  message,
} from 'antd';
import { identity, cloneDeep } from 'lodash';

import { OverlayLoading } from 'components/overlay-loading';
import PdButton from 'components/pd-button';

import RiskManagementService from 'services/risk-management';

import { errHandling } from 'utils';
import {
  percentageInputNumberFormatter,
  percentageInputNumberParser,
  priceInputNumberFormatter,
  priceInputNumberParser,
} from 'utils/inputNumber.js';

import './style.scss';

const { Text, Title } = Typography;
const { Content } = Layout;
const { Option } = Select;

const FIELDS = {
  DEPOSIT: {},
  TITLE: 'title',
  RISK_ATTRIBUTES: 'rent_duration_risks_attributes',
  RISK_TYPE: 'risk_type',
};

export function RiskCreateEdit({ closeModal = identity, risk = {} }) {
  const [form] = Form.useForm();
  const [depositType, setDepositType] = useState('percentage');
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [rentDurationsList, setRentDurationsList] = useState([]);
  const [value, setValue] = useState({});
  const [riskAttributes, setRiskAttributes] = useState([]);

  const { id: riskId = '', title = '', risk_type } = risk;

  const formItemLayout = {
    labelCol: { span: 12 },
    wrapperCol: { span: 12 },
    labelAlign: 'left',
  };

  const populateRiskAttributes = (id, type) => {
    const deposit = riskAttributes.find((risk) => risk.rent_duration_id === id);
    if (deposit) {
      return deposit[type];
    }

    return null;
  };

  // for toggling render deposit inputNumber
  const depositTypeFormList = {
    percentage: {
      render(id) {
        return (
          <InputNumber
            className="w-full"
            formatter={percentageInputNumberFormatter}
            parser={percentageInputNumberParser}
            onChange={(value) => changeValue(id, value)}
            min={0}
          />
        );
      },
    },
    fixed: {
      render(id) {
        return (
          <InputNumber
            className="w-full"
            formatter={priceInputNumberFormatter}
            parser={priceInputNumberParser}
            onChange={(value) => changeValue(id, value)}
            min={0}
          />
        );
      },
    },
  };

  async function onFinish(values) {
    setLoadingButton(true);
    try {
      const payload = {
        risk: {
          title: values.title,
          risk_type: depositType,
          rent_duration_risks_attributes: riskAttributes,
        },
      };

      if (riskId) {
        await RiskManagementService.updateRisk(riskId, payload);
      } else {
        await RiskManagementService.createRisk(payload);
      }

      message.success(
        riskId ? 'Successfully edit risk' : 'Successfully add new risk'
      );

      closeModal();
    } catch (err) {
      errHandling(err);
    } finally {
      setLoadingButton(false);
    }
  }

  function changeValue(rent_duration_id, value) {
    const copyRiskAttributes = cloneDeep(riskAttributes);
    const key =
      depositType === 'percentage' ? 'deposit_percentage' : 'deposit_fixed';
    const unselectedKey =
      depositType === 'percentage' ? 'deposit_fixed' : 'deposit_percentage';
    const findExisting = copyRiskAttributes.find(
      (risk) => risk.rent_duration_id === rent_duration_id
    );
    if (findExisting) {
      findExisting[key] = value;
    } else {
      copyRiskAttributes.push({
        rent_duration_id,
        [key]: value,
        [unselectedKey]: 0,
      });
    }
    setRiskAttributes(copyRiskAttributes);
  }

  function handleOnChangeRadio({ target: { value } = {} } = {}) {
    form.setFieldsValue({ deposit: '' });
    setDepositType(value);
  }

  async function fetchRentDurations() {
    setLoadingPage(true);
    try {
      const {
        data: { data = [] },
      } = await RiskManagementService.getRentDurationList();
      data.forEach((rentDuration) => {
        FIELDS.DEPOSIT[rentDuration.duration] = `${rentDuration.duration}_days`;
      });
      setRentDurationsList(data);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadingPage(false);
    }
  }

  async function getDetailRisk() {
    setLoadingPage(true);
    try {
      const {
        data: { data },
      } = await RiskManagementService.getRiskDetail(riskId);
      const activeRentDurationRisk = [];
      data.rent_duration_risks.forEach(
        ({ deposit_fixed, deposit_percentage, rent_duration, id }) => {
          if (rent_duration.status === 'active') {
            activeRentDurationRisk.push({
              deposit_fixed,
              deposit_percentage,
              rent_duration_id: rent_duration.id,
              id,
            });
          }
        }
      );
      setValue({
        title,
        risk_type,
      });
      setRiskAttributes(activeRentDurationRisk);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadingPage(false);
    }
  }

  useEffect(() => {
    fetchRentDurations();
    if (riskId) {
      getDetailRisk();
      setDepositType(risk_type);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Content>
      {loadingPage && <OverlayLoading open={loadingPage} />}
      <Form
        className="pd-risk-management-form"
        form={form}
        onFinish={onFinish}
        initialValues={{
          [FIELDS.TITLE]: value[FIELDS.TITLE],
        }}>
        <div className="section-container">
          <Title level={4} className="pd-margin-bottom-lg">
            {riskId ? 'Edit' : 'Create'} Risk
          </Title>
          <div className="inner-section">
            <Form.Item
              label="Title"
              layout="vertical"
              name={FIELDS.TITLE}
              rules={[
                {
                  message: 'Please input risk title!',
                  required: true,
                },
              ]}>
              <Input placeholder="Input title here" />
            </Form.Item>

            <Form.Item>
              <Radio.Group onChange={handleOnChangeRadio} value={depositType}>
                <Radio value="percentage">Percentage</Radio>
                <Radio value="fixed">Fix Price</Radio>
              </Radio.Group>
            </Form.Item>

            <Row className="pd-margin-bottom-md">
              <Col lg={formItemLayout.labelCol}>
                <Text strong>Durations</Text>
              </Col>
              <Col lg={formItemLayout.wrapperCol}>
                <Text strong>Price</Text>
              </Col>
            </Row>

            <Row className="deposit-form-container pd-margin-bottom-md">
              {rentDurationsList.map(({ id, duration }) => (
                <Form.Item
                  key={id}
                  className="pd-margin-bottom-sm w-full"
                  label={`${duration} days`}
                  name={`${FIELDS.DEPOSIT[duration]}_${depositType}`}
                  {...formItemLayout}
                  initialValue={populateRiskAttributes(
                    id,
                    depositType === 'percentage'
                      ? 'deposit_percentage'
                      : 'deposit_fixed'
                  )}>
                  {depositTypeFormList[depositType].render(id)}
                </Form.Item>
              ))}
            </Row>
          </div>
        </div>

        {depositType === 'percentage' && (
          <div className="pd-margin-bottom-lg">
            <Text>Notes*</Text> <br />
            <Text>Deposit fee is calculated based on price of the product</Text>
          </div>
        )}

        <div className="button-container">
          <Form.Item>
            <Row type="flex" justify="center">
              <PdButton
                className="w-half height-32"
                full
                htmlType="submit"
                loading={loadingButton}
                type="primary">
                Save
              </PdButton>
            </Row>
          </Form.Item>
        </div>
      </Form>
    </Content>
  );
}
